/* eslint-disable react/require-default-props */
import React, { useMemo } from 'react';
import { SVGIcon } from '@superops/super-icons';
import { Page, DisplayText } from '@superops/ui-design-system';
import { StatementCards } from '@nile/reports/src/modules/InvoiceStatements/StatementCards';
import { StatementPreviewTable } from '@nile/reports/src/modules/InvoiceStatements/StatementPreviewTable';
import { useInvoiceStatementsStore } from '@nile/reports/src/modules/InvoiceStatements/invoiceStatementsStore';
import { FormattedMessage } from 'react-intl';
import { DateTimeFormatter } from '@nile/components';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import { AccountClient, GET_INVOICE_ADDRESS } from '@nile/graphql';
import { SanitizedAddress } from '@nile/client_management/src/Utils/invoiceAndQuoteHelper';

export const StatementPreview = (): JSX.Element => {
  const computeDateRange = (
    input:
      | { start: string; end: string }
      | { operator?: string; value?: unknown }
      | undefined
  ): { start: string; end: string } | undefined => {
    if (!input) return undefined;
    // If already a range
    if ('start' in (input as any) && 'end' in (input as any)) {
      const { start, end } = input as { start: string; end: string };
      return { start, end };
    }
    const condition = input as { operator?: string; value?: unknown };
    const operator = condition?.operator;
    const value = condition?.value;
    if (!operator) return undefined;

    if (operator === 'between' && Array.isArray(value) && value.length === 2) {
      return {
        start: dayjs(value[0]).toISOString(),
        end: dayjs(value[1]).toISOString()
      };
    }
    if (operator === 'on' && typeof value === 'string') {
      const now = dayjs();
      const startOfDay = now.startOf('day');
      const endOfDay = now.endOf('day');
      switch (value) {
        case 'placeholder.today':
          return {
            start: startOfDay.toISOString(),
            end: endOfDay.toISOString()
          };
        case 'placeholder.yesterday': {
          const s = now.subtract(1, 'day').startOf('day');
          const e = now.subtract(1, 'day').endOf('day');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.this.week': {
          const s = now.startOf('isoWeek');
          const e = now.endOf('isoWeek');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.last.week': {
          const s = now.subtract(1, 'week').startOf('isoWeek');
          const e = now.subtract(1, 'week').endOf('isoWeek');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.this.month': {
          const s = now.startOf('month');
          const e = now.endOf('month');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.last.month': {
          const s = now.subtract(1, 'month').startOf('month');
          const e = now.subtract(1, 'month').endOf('month');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.last.30.days': {
          const s = now.subtract(30, 'day').startOf('day');
          const e = endOfDay;
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.this.quarter': {
          const currentMonth = now.month();
          const qStartMonth = Math.floor(currentMonth / 3) * 3;
          const s = now.month(qStartMonth).startOf('month');
          const e = s.add(2, 'month').endOf('month');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.last.quarter': {
          const currentMonth = now.month();
          let qStartMonth = Math.floor(currentMonth / 3) * 3 - 3;
          let year = now.year();
          if (qStartMonth < 0) {
            qStartMonth = 9;
            year -= 1;
          }
          const s = now
            .year(year)
            .month(qStartMonth)
            .startOf('month');
          const e = s.add(2, 'month').endOf('month');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        case 'placeholder.last.six.months': {
          const s = now.subtract(6, 'month').startOf('month');
          const e = now.endOf('month');
          return { start: s.toISOString(), end: e.toISOString() };
        }
        default:
          break;
      }
    }
    if (operator === 'on' && value) {
      const d = dayjs(value as string);
      return {
        start: d.startOf('day').toISOString(),
        end: d.endOf('day').toISOString()
      };
    }
    return undefined;
  };

  const {
    setClient,
    clientName,
    setClientName,
    client,
    reportAllowedDateRange,
    resetReportClientFilter
  } = useInvoiceStatementsStore();

  const conditions = useMemo(
    () => (reportAllowedDateRange ? [reportAllowedDateRange] : []),
    [reportAllowedDateRange]
  );

  const { data: invoiceAddressData, error: invoiceAddressError } = useQuery(
    GET_INVOICE_ADDRESS,
    {
      client: AccountClient,
      variables: {
        input: client ? { client: { accountId: client } } : undefined
      },
      fetchPolicy: 'no-cache'
    }
  );

  return (
    <Page fullWidth variant="secondary" contentHeight="1px">
      <div className="fx fw actr p2040 g20 border-b-gray-300">
        <span
          className="ptr"
          onClick={() => {
            setClient(null);
            setClientName('');
            resetReportClientFilter();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              setClient(null);
              setClientName('');
              resetReportClientFilter();
            }
          }}
          role="button"
          tabIndex={0}
        >
          <SVGIcon name="BackArrow" />
        </span>
        <div>
          <DisplayText
            element="p"
            size="large"
            variant="subdued"
            weight="SemiBold"
          >
            <FormattedMessage id="invoice.statements.preview" />
          </DisplayText>
          <DisplayText element="p" size="extraLarge" weight="SemiBold">
            {clientName}
          </DisplayText>
        </div>
      </div>
      <div className="m20" />
      <div className="m20">
        {/* Statement header */}
        <div className="tw-m-4 tw-p-4">
          <div className="tw-flex tw-items-start tw-justify-between">
            {/* Left: MSP details */}
            <div className="tw-text-primary statement-address">
              {!invoiceAddressError &&
              invoiceAddressData?.getInvoiceAddress?.sender ? (
                <SanitizedAddress
                  address={invoiceAddressData.getInvoiceAddress.sender}
                />
              ) : null}
            </div>
            {/* Right: Account statement meta */}
            <div className="tw-text-right">
              <DisplayText element="p" size="large" weight="SemiBold">
                <FormattedMessage id="invoice.statements.accountStatement" />
              </DisplayText>
              <div className="tw-mt-1">
                <div className="tw-text-secondary">
                  <span className="tw-text-tertiary">
                    <FormattedMessage id="invoice.statements.statementDate" />
                    {': '}
                  </span>
                  <DateTimeFormatter
                    type="date"
                    date={new Date().toISOString()}
                  />
                </div>
                {reportAllowedDateRange && (
                  <div className="tw-text-secondary">
                    <span className="tw-text-tertiary">
                      <FormattedMessage id="invoice.statements.period" />
                      {': '}
                    </span>
                    {(() => {
                      const range = computeDateRange(reportAllowedDateRange);
                      if (!range) return null;
                      const isSameDay = dayjs(range.start).isSame(
                        dayjs(range.end),
                        'day'
                      );
                      if (isSameDay) {
                        return (
                          <DateTimeFormatter type="date" date={range.end} />
                        );
                      }
                      return (
                        <>
                          <DateTimeFormatter type="date" date={range.start} />
                          <span className="tw-mx-1">-</span>
                          <DateTimeFormatter type="date" date={range.end} />
                        </>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Account Summary */}
        <div className="tw-m-4 tw-px-4">
          <DisplayText element="p" size="extraLarge" weight="SemiBold">
            <FormattedMessage id="invoice.statements.accountSummary" />
          </DisplayText>
        </div>
        <div className="m20 p20">
          <StatementCards isPreviewPage conditions={conditions} />
        </div>
      </div>

      <StatementPreviewTable />
    </Page>
  );
};
